import React, { useState } from 'react';
import { Card, Form, Input, Button, message, Descriptions, Avatar, Space, Divider } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useAuthStore } from '../store/authStore';
import api from '../utils/api';

const Profile = () => {
    const { t } = useTranslation();
    const { user, setUser } = useAuthStore();
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const [passwordForm] = Form.useForm();

    const updatePasswordMutation = useMutation(
        (data) => api.put('/auth/update-password', data),
        {
            onSuccess: () => {
                message.success(t('passwordUpdatedSuccessfully'));
                setIsEditingPassword(false);
                passwordForm.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToUpdatePassword'));
            },
        }
    );

    const handlePasswordUpdate = (values) => {
        if (values.newPassword !== values.confirmPassword) {
            message.error(t('passwordsDoNotMatch'));
            return;
        }
        updatePasswordMutation.mutate({
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
        });
    };

    return (
        <div>
            <h1 style={{ marginBottom: 24 }}>{t('profile')}</h1>

            <Card style={{ maxWidth: 800 }}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div style={{ textAlign: 'center' }}>
                        <Avatar size={100} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                        <h2 style={{ marginTop: 16, marginBottom: 0 }}>{user?.name || user?.username}</h2>
                        <p style={{ color: '#666', textTransform: 'capitalize' }}>{user?.role}</p>
                    </div>

                    <Divider />

                    <Descriptions title={t('personalInformation')} bordered column={1}>
                        <Descriptions.Item label={t('name')}>
                            {user?.name || user?.username}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('email')}>
                            <Space>
                                <MailOutlined />
                                {user?.email || t('notProvided')}
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label={t('phone')}>
                            <Space>
                                <PhoneOutlined />
                                {user?.phone || t('notProvided')}
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label={t('role')}>
                            {user?.role}
                        </Descriptions.Item>
                        {user?.academy && (
                            <Descriptions.Item label={t('academy')}>
                                {user.academy.name}
                            </Descriptions.Item>
                        )}
                    </Descriptions>

                    <Divider />

                    <div>
                        <h3>
                            <LockOutlined /> {t('changePassword')}
                        </h3>
                        {!isEditingPassword ? (
                            <Button type="primary" onClick={() => setIsEditingPassword(true)}>
                                {t('updatePassword')}
                            </Button>
                        ) : (
                            <Form
                                form={passwordForm}
                                layout="vertical"
                                onFinish={handlePasswordUpdate}
                                style={{ marginTop: 16 }}
                            >
                                <Form.Item
                                    name="currentPassword"
                                    label={t('currentPassword')}
                                    rules={[{ required: true, message: t('pleaseEnterCurrentPassword') }]}
                                >
                                    <Input.Password prefix={<LockOutlined />} />
                                </Form.Item>

                                <Form.Item
                                    name="newPassword"
                                    label={t('newPassword')}
                                    rules={[
                                        { required: true, message: t('pleaseEnterNewPassword') },
                                        { min: 6, message: t('passwordMustBeAtLeast6Characters') }
                                    ]}
                                >
                                    <Input.Password prefix={<LockOutlined />} />
                                </Form.Item>

                                <Form.Item
                                    name="confirmPassword"
                                    label={t('confirmPassword')}
                                    rules={[{ required: true, message: t('pleaseConfirmPassword') }]}
                                >
                                    <Input.Password prefix={<LockOutlined />} />
                                </Form.Item>

                                <Form.Item>
                                    <Space>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={updatePasswordMutation.isLoading}
                                        >
                                            {t('save')}
                                        </Button>
                                        <Button onClick={() => {
                                            setIsEditingPassword(false);
                                            passwordForm.resetFields();
                                        }}>
                                            {t('cancel')}
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Form>
                        )}
                    </div>
                </Space>
            </Card>
        </div>
    );
};

export default Profile;
